import React, { useEffect, useState } from 'react';
import './EvaluationForm.css';

function EvaluationForm({ onSubmit, loading, initialValues = {} }) {
  const defaults = {
    monthly_take_home_income: '',
    current_savings: '',
    monthly_fixed_expenses: '',
    monthly_debt_payments: '',
    total_debt_balance: '',
    purchase_price: '',
    payment_type: 'finance',
    down_payment: '',
    apr: '',
    term_months: '',
    purchase_description: '',
    purchase_category: 'other',
    credit_score: '',
    employment_status: 'employed',
    years_in_current_job: '',
    emergency_fund_target_months: '6',
    expected_annual_income_growth: '0',
    years_until_retirement: '',
    major_expenses_next_12_months: ''
  };
  const [formData, setFormData] = useState({ ...defaults, ...initialValues });

  const [showOptional, setShowOptional] = useState(false);

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setFormData((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  useEffect(() => {
    if (initialValues?.purchase_price) {
      const formEl = document.querySelector('.evaluation-form');
      if (formEl) {
        formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.monthly_take_home_income || !formData.current_savings || 
        !formData.purchase_price || !formData.payment_type) {
      alert('Please fill in all required fields');
      return;
    }

    if ((formData.payment_type === 'finance' || formData.payment_type === 'mixed') &&
        (!formData.down_payment || !formData.apr || !formData.term_months)) {
      alert('Please fill in financing details (down payment, APR, term)');
      return;
    }

    // Convert to numbers
    const submitData = {
      monthly_take_home_income: parseFloat(formData.monthly_take_home_income),
      current_savings: parseFloat(formData.current_savings),
      monthly_fixed_expenses: formData.monthly_fixed_expenses ? parseFloat(formData.monthly_fixed_expenses) : undefined,
      monthly_debt_payments: formData.monthly_debt_payments ? parseFloat(formData.monthly_debt_payments) : undefined,
      total_debt_balance: formData.total_debt_balance ? parseFloat(formData.total_debt_balance) : undefined,
      purchase_price: parseFloat(formData.purchase_price),
      payment_type: formData.payment_type,
      down_payment: formData.down_payment ? parseFloat(formData.down_payment) : undefined,
      apr: formData.apr ? parseFloat(formData.apr) : undefined,
      term_months: formData.term_months ? parseInt(formData.term_months) : undefined,
      purchase_description: formData.purchase_description || undefined,
      purchase_category: formData.purchase_category || undefined,
      credit_score: formData.credit_score ? parseFloat(formData.credit_score) : undefined,
      employment_status: formData.employment_status,
      years_in_current_job: formData.years_in_current_job ? parseFloat(formData.years_in_current_job) : undefined,
      emergency_fund_target_months: parseInt(formData.emergency_fund_target_months) || 6,
      expected_annual_income_growth: formData.expected_annual_income_growth ? parseFloat(formData.expected_annual_income_growth) : 0,
      years_until_retirement: formData.years_until_retirement ? parseFloat(formData.years_until_retirement) : undefined,
      major_expenses_next_12_months: formData.major_expenses_next_12_months ? parseFloat(formData.major_expenses_next_12_months) : 0
    };

    onSubmit(submitData);
  };

  return (
    <div className="evaluation-form">
      <div className="card">
        <h2>📋 Financial Snapshot</h2>
        <p className="section-subtitle">Tell us about your finances</p>

        <form onSubmit={handleSubmit}>
          {/* Required Financial Info */}
          <div className="form-section">
            <h3>Required Information</h3>

            <div className="form-group">
              <label htmlFor="income">Monthly Take-Home Income *</label>
              <input
                type="number"
                id="income"
                name="monthly_take_home_income"
                value={formData.monthly_take_home_income}
                onChange={handleChange}
                placeholder="e.g., 5000"
                min="0"
                step="100"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="savings">Current Savings *</label>
              <input
                type="number"
                id="savings"
                name="current_savings"
                value={formData.current_savings}
                onChange={handleChange}
                placeholder="e.g., 15000"
                min="0"
                step="100"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="price">Purchase Price *</label>
              <input
                type="number"
                id="price"
                name="purchase_price"
                value={formData.purchase_price}
                onChange={handleChange}
                placeholder="e.g., 25000"
                min="0"
                step="100"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="payment_type">Payment Type *</label>
              <select
                id="payment_type"
                name="payment_type"
                value={formData.payment_type}
                onChange={handleChange}
                required
              >
                <option value="cash">Cash (full payment)</option>
                <option value="finance">Financing (loan)</option>
                <option value="mixed">Mixed (down payment + financing)</option>
              </select>
            </div>
          </div>

          {/* Financing Details (conditional) */}
          {(formData.payment_type === 'finance' || formData.payment_type === 'mixed') && (
            <div className="form-section">
              <h3>Financing Details</h3>

              <div className="form-group.inline">
                <div>
                  <label htmlFor="down_payment">Down Payment {formData.payment_type === 'finance' ? '(optional)' : '*'}</label>
                  <input
                    type="number"
                    id="down_payment"
                    name="down_payment"
                    value={formData.down_payment}
                    onChange={handleChange}
                    placeholder="e.g., 5000"
                    min="0"
                    step="100"
                    required={formData.payment_type === 'mixed'}
                  />
                </div>

                <div>
                  <label htmlFor="apr">APR (%) *</label>
                  <input
                    type="number"
                    id="apr"
                    name="apr"
                    value={formData.apr}
                    onChange={handleChange}
                    placeholder="e.g., 6.5"
                    min="0"
                    max="50"
                    step="0.1"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="term">Loan Term (months) *</label>
                <input
                  type="number"
                  id="term"
                  name="term_months"
                  value={formData.term_months}
                  onChange={handleChange}
                  placeholder="e.g., 60"
                  min="1"
                  step="1"
                  required
                />
              </div>
            </div>
          )}

          {/* Optional Information */}
          <div className="form-section">
            <button
              type="button"
              className="toggle-btn"
              onClick={() => setShowOptional(!showOptional)}
            >
              {showOptional ? '▼' : '▶'} Optional Details
            </button>

            {showOptional && (
              <>
                <div className="form-group">
                  <label htmlFor="expenses">Monthly Fixed Expenses</label>
                  <input
                    type="number"
                    id="expenses"
                    name="monthly_fixed_expenses"
                    value={formData.monthly_fixed_expenses}
                    onChange={handleChange}
                    placeholder="e.g., 2000 (rent, utilities, insurance, etc.)"
                    min="0"
                    step="100"
                  />
                  <small>If not provided, will be estimated from your income bracket</small>
                </div>

                <div className="form-group">
                  <label htmlFor="debt_payments">Monthly Debt Payments</label>
                  <input
                    type="number"
                    id="debt_payments"
                    name="monthly_debt_payments"
                    value={formData.monthly_debt_payments}
                    onChange={handleChange}
                    placeholder="e.g., 500"
                    min="0"
                    step="50"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="debt_balance">Total Debt Balance</label>
                  <input
                    type="number"
                    id="debt_balance"
                    name="total_debt_balance"
                    value={formData.total_debt_balance}
                    onChange={handleChange}
                    placeholder="e.g., 8000"
                    min="0"
                    step="100"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Purchase Description</label>
                  <textarea
                    id="description"
                    name="purchase_description"
                    value={formData.purchase_description}
                    onChange={handleChange}
                    placeholder="e.g., Used Toyota Camry 2019"
                    rows="3"
                  />
                </div>

            <div className="form-group">
              <label htmlFor="category">Purchase Category</label>
              <select
                id="category"
                name="purchase_category"
                value={formData.purchase_category}
                onChange={handleChange}
              >
                <option value="other">Other / Not Sure</option>
                <option value="essential">Essential (home, vehicle, etc.)</option>
                <option value="luxury">Luxury (high-end item)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="credit_score">Credit Score (optional)</label>
              <input
                type="number"
                id="credit_score"
                name="credit_score"
                value={formData.credit_score}
                onChange={handleChange}
                placeholder="e.g., 750 (300-850)"
                min="300"
                max="850"
                step="1"
              />
              <small>Helps us understand your borrowing health</small>
            </div>

            <div className="form-group">
              <label htmlFor="employment_status">Employment Status</label>
              <select
                id="employment_status"
                name="employment_status"
                value={formData.employment_status}
                onChange={handleChange}
              >
                <option value="employed">Full-time Employed</option>
                <option value="self-employed">Self-Employed</option>
                <option value="part-time">Part-time Employed</option>
                <option value="unemployed">Currently Unemployed</option>
                <option value="retired">Retired</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="years_in_job">Years in Current Job/Role</label>
              <input
                type="number"
                id="years_in_job"
                name="years_in_current_job"
                value={formData.years_in_current_job}
                onChange={handleChange}
                placeholder="e.g., 3"
                min="0"
                step="0.5"
              />
              <small>Employment stability matters for loan qualification</small>
            </div>

            <div className="form-group">
              <label htmlFor="emergency_fund">Emergency Fund Target (months of expenses)</label>
              <select
                id="emergency_fund"
                name="emergency_fund_target_months"
                value={formData.emergency_fund_target_months}
                onChange={handleChange}
              >
                <option value="3">3 months (minimum)</option>
                <option value="6">6 months (recommended)</option>
                <option value="9">9 months</option>
                <option value="12">12 months (conservative)</option>
              </select>
              <small>How much emergency savings do you want to keep?</small>
            </div>

            <div className="form-group">
              <label htmlFor="income_growth">Expected Annual Income Growth (%)</label>
              <input
                type="number"
                id="income_growth"
                name="expected_annual_income_growth"
                value={formData.expected_annual_income_growth}
                onChange={handleChange}
                placeholder="e.g., 2"
                min="-10"
                max="50"
                step="0.5"
              />
              <small>Realistic expectation for salary increases (optional)</small>
            </div>

            <div className="form-group">
              <label htmlFor="years_retirement">Years Until Retirement</label>
              <input
                type="number"
                id="years_retirement"
                name="years_until_retirement"
                value={formData.years_until_retirement}
                onChange={handleChange}
                placeholder="e.g., 25"
                min="0"
                step="1"
              />
              <small>Long-term loans should fit within your working years</small>
            </div>

            <div className="form-group">
              <label htmlFor="major_expenses">Expected Major Expenses (next 12 months)</label>
              <input
                type="number"
                id="major_expenses"
                name="major_expenses_next_12_months"
                value={formData.major_expenses_next_12_months}
                onChange={handleChange}
                placeholder="e.g., 3000 (medical, home repairs, etc.)"
                min="0"
                step="100"
              />
              <small>Upcoming expenses that might impact cash flow</small>
            </div>
              </>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={loading}
              className="submit-btn"
            >
              {loading ? 'Calculating...' : '📊 Evaluate Affordability'}
            </button>
          </div>

          <p className="form-note">
            * Required fields. All information is used for calculation only and not stored personally.
          </p>
        </form>
      </div>
    </div>
  );
}

export default EvaluationForm;
